import React, { useCallback, useMemo } from "react";

import { faTrashCan } from "@performyard/fontawesome/pro-solid-svg-icons";
import { AvatarProfile, ButtonIcon, InputWrapper, Text } from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../../components/Inputs/SelectEmployee";

import {
	useAddCompensationCycleOwner,
	useCompensationSettings,
	useRbacPrimaryRolePermissions,
	useRemoveCompensationCycleOwner,
} from "../../../../api/hooks";

import { fetchEmployees } from "../../../../api/services";

const ROLE_SORT_ORDER: Record<string, number> = {
	"Billing Admin": 0,
	Admin: 1,
};

const CompensationCycleOwners = () => {
	const { data: compensationSettings } = useCompensationSettings();
	const { mutate: addCycleOwner } = useAddCompensationCycleOwner();
	const { mutate: removeCycleOwner } = useRemoveCompensationCycleOwner();
	const { data: permissions, isLoading: isCompensationPermissionsLoading } =
		useRbacPrimaryRolePermissions("compensation");

	const allAdminIds = useMemo(() => {
		const admins = compensationSettings?.admins?.map((u) => u._id) ?? [];
		const billingAdmins = compensationSettings?.billingAdmins?.map((u) => u._id) ?? [];
		const basicAdmins = compensationSettings?.basicAdmins?.map((u) => u._id) ?? [];
		return [...admins, ...billingAdmins, ...basicAdmins].filter((id): id is string => Boolean(id));
	}, [compensationSettings]);

	const rawCycleOwners = compensationSettings?.cycleOwners ?? [];
	const cycleOwners = useMemo(() => {
		return [...rawCycleOwners]
			.map((owner, index) => ({ ...owner, originalIndex: index }))
			.sort((a, b) => {
				const roleA = ROLE_SORT_ORDER[a.role] ?? 999;
				const roleB = ROLE_SORT_ORDER[b.role] ?? 999;
				// Billing Admins and Admins sort by role first
				const isAdminGroupA = roleA <= 1;
				const isAdminGroupB = roleB <= 1;
				if (isAdminGroupA && !isAdminGroupB) return -1;
				if (!isAdminGroupA && isAdminGroupB) return 1;
				if (isAdminGroupA && isAdminGroupB) return roleA - roleB;
				// Basic Admins and Employees: sort by original order (date added)
				return a.originalIndex - b.originalIndex;
			});
	}, [rawCycleOwners]);
	const canUpdateCompensationSettings = isCompensationPermissionsLoading
		? undefined
		: permissions?.includes("compensation:updateAdminSettings");

	const handleAddOwner = (employeeId: string | null, employeeName: string | null) => {
		if (!employeeId || !employeeName) return;
		const alreadyExists = cycleOwners.some((o) => o._id === employeeId);
		if (alreadyExists) return;

		addCycleOwner({
			userId: employeeId,
			userName: employeeName,
		});
	};

	const handleRemoveOwner = (ownerId: string) => {
		const owner = cycleOwners.find((o) => o._id === ownerId);
		if (owner?.isAdmin) return;

		removeCycleOwner({ userId: ownerId });
	};

	const fetchFilteredEmployees = useCallback(
		async (searchTerm: string) => {
			if (allAdminIds.length === 0) {
				return [];
			}
			const ownerIds = new Set(cycleOwners.map((o) => o._id));
			const data = await fetchEmployees({
				searchTerm,
				filters: { _id: allAdminIds },
			});
			return (
				data.users
					?.filter((user) => !ownerIds.has(user._id))
					.map((user) => ({ id: user._id, name: user.name })) ?? []
			);
		},
		[cycleOwners, allAdminIds]
	);

	return (
		<div className="w-full">
			<InputWrapper id="cycle-owners" label="Add Compensation Cycle Owners">
				<SingleSelectEmployee
					id="cycle-owners"
					value={null}
					onChange={handleAddOwner}
					fetchEmployees={fetchFilteredEmployees}
					placeholder="Select cycle owners"
					isClearable={false}
					disabled={canUpdateCompensationSettings === false}
				/>
			</InputWrapper>
			<ul className="mt-4 border-t border-solid border-neutral-2">
				{cycleOwners.map((owner) => (
					<li
						key={owner._id}
						className="grid h-10 grid-cols-[1fr_1fr_24px] items-center gap-2 border-b border-solid border-neutral-2 px-4"
					>
						<AvatarProfile
							employeeId={owner._id}
							employeeName={owner.name}
							avatarSize={20}
							textSize="paragraph-3"
							noLink
						/>
						<Text size="paragraph-3" weight="regular" color="neutral-9">
							{owner.role}
						</Text>
						<div className="flex justify-end">
							{!owner.isAdmin && (
								<ButtonIcon
									icon={faTrashCan}
									color="neutral"
									shade={5}
									size={16}
									variant="standard"
									onClick={() => handleRemoveOwner(owner._id)}
									aria-label="Remove owner"
								/>
							)}
						</div>
					</li>
				))}
			</ul>
		</div>
	);
};

export default CompensationCycleOwners;
